import React, { createContext, useContext } from "react";
import MuiDrawer, { type DrawerProps } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ChevronLeftOutlined from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import MuiListItemButton, { type ListItemButtonProps } from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MuiTooltip from "@mui/material/Tooltip";
import { bg, text, navColors } from "../../theme/tokens";

export interface SidebarContextValue {
  open: boolean;
  collapsedWidth: number;
  expandedWidth: number;
}

const SidebarContext = createContext<SidebarContextValue>({
  open: true,
  collapsedWidth: 48,
  expandedWidth: 240,
});

export function useSidebar() {
  return useContext(SidebarContext);
}

export interface SidebarNavProps
  extends Omit<DrawerProps, "open" | "variant" | "onClose"> {
  /** Whether the sidebar is expanded */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Width when expanded (default: 240) */
  expandedWidth?: number;
  /** Width when collapsed — icon-only (default: 64) */
  collapsedWidth?: number;
  /** Expand behavior:
   *  - "permanent": click to toggle, stays open/closed
   *  - "hover": collapsed by default, expands on mouse enter, collapses on mouse leave
   */
  behavior?: "permanent" | "hover";
  /** Whether to show the edge toggle button (default: true for "permanent", false for "hover") */
  showToggle?: boolean;
  /** Content rendered inside the sidebar */
  children: React.ReactNode;
  /** Optional header slot — rendered above children */
  header?: React.ReactNode;
  /** Optional footer slot — rendered at bottom */
  footer?: React.ReactNode;
}

export function SidebarNav({
  open,
  onOpenChange,
  expandedWidth = 240,
  collapsedWidth = 48,
  behavior = "permanent",
  showToggle,
  children,
  header,
  footer,
  sx,
  ...rest
}: SidebarNavProps) {
  const isHover = behavior === "hover";
  const drawerWidth = open ? expandedWidth : collapsedWidth;
  const layoutWidth = isHover ? collapsedWidth : drawerWidth;
  const shouldShowToggle = showToggle ?? !isHover;

  const hoverHandlers = isHover
    ? {
        onMouseEnter: () => onOpenChange(true),
        onMouseLeave: () => onOpenChange(false),
      }
    : {};

  return (
    <SidebarContext.Provider value={{ open, collapsedWidth, expandedWidth }}>
      <Box sx={{ position: "relative", width: layoutWidth, flexShrink: 0 }}>
        <MuiDrawer
          variant="permanent"
          {...rest}
          {...hoverHandlers}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              transition: "width 200ms ease-in-out",
              overflowX: "hidden",
              boxSizing: "border-box",
              ...(isHover && { zIndex: (theme: any) => theme.zIndex.drawer + 2 }),
            },
            ...sx,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {header && <Box sx={{ flexShrink: 0 }}>{header}</Box>}
            <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
              {children}
            </Box>
            {footer && <Box sx={{ flexShrink: 0 }}>{footer}</Box>}
          </Box>
        </MuiDrawer>

        {shouldShowToggle && (
          <IconButton
            size="small"
            onClick={() => onOpenChange(!open)}
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translate(50%, -50%)",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              width: 28,
              height: 28,
              border: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
              boxShadow: 1,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
              },
            }}
          >
            {open ? <ChevronLeftOutlined /> : <ChevronRightOutlined />}
          </IconButton>
        )}
      </Box>
    </SidebarContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// SidebarItem
// ---------------------------------------------------------------------------

export interface SidebarItemProps extends ListItemButtonProps {
  /** Icon element (required — visible in both expanded and collapsed states) */
  icon: React.ReactNode;
  /** Label text */
  label: string;
  /** Optional content rendered on the right side when expanded (badge, count, etc.) */
  endAdornment?: React.ReactNode;
}

const itemSx = {
  borderRadius: 1,
  backgroundColor: bg.primary,
  color: text.secondary,
  "& .MuiListItemIcon-root": {
    color: navColors.itemIconFg,
  },
  "&:hover": {
    backgroundColor: bg.primaryHover,
  },
  "&.Mui-selected": {
    backgroundColor: bg.active,
    color: text.primary,
    "& .MuiListItemIcon-root": {
      color: navColors.itemIconFgActive,
    },
    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
    "&:hover": {
      backgroundColor: bg.primaryHover,
    },
  },
};

export function SidebarItem({
  icon,
  label,
  endAdornment,
  sx,
  ...rest
}: SidebarItemProps) {
  const { open } = useSidebar();

  if (!open) {
    return (
      <MuiTooltip title={label} placement="right" arrow>
        <MuiListItemButton
          {...rest}
          sx={{
            ...itemSx,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 40,
            height: 40,
            padding: 0,
            margin: "0 auto",
            ...sx,
          }}
        >
          <ListItemIcon sx={{ minWidth: "unset", margin: 0, "& .MuiSvgIcon-root": { fontSize: 20 } }}>
            {icon}
          </ListItemIcon>
        </MuiListItemButton>
      </MuiTooltip>
    );
  }

  return (
    <MuiListItemButton {...rest} sx={{ ...itemSx, height: 40, ...sx }}>
      <ListItemIcon sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}>{icon}</ListItemIcon>
      <ListItemText primary={label} />
      {endAdornment}
    </MuiListItemButton>
  );
}
